import { getConfig } from "./config";
import { cookies } from "next/headers";
import { redirect } from "next/navigation";
import { decryptAuthToken, revalidatedTokens } from "./tokens";
import { IntrasoftJwtTokens } from "@intrasoft/auth-core";

/**
 * Authenticates a user by checking their cookies and tokens.
 *
 * @returns {Promise<T>} The authenticated user object.
 * @throws {Error} If the user is not authenticated or if the tokens cannot be re-fetched.
 *
 * @example
 * ```typescript
 * const user = await auth<User>();
 * console.log(user);
 * ```
 */
export async function auth<T = any>(): Promise<T> {
  // Retrieve configuration
  const config = getConfig();
  // Retrieve cookies
  const _c = await cookies();

  // Extract tokens from cookies
  const tokens = {
    access: _c.get(config.accessCookie)?.value,
    refresh: _c.get(config.accessCookie)?.value,
  };

  // Check if user is authenticated
  if (!isAuthenticated(tokens)) {
    return redirect(config.signInUrl);
  }

  // Refetch tokens
  await refetchTokens(tokens as IntrasoftJwtTokens);

  // Decrypt access token to get user object
  const { payload: user } = await decryptAuthToken(tokens.access!);

  // If user object is not valid, redirect to sign-in URL
  if (!user) {
    return redirect(config.signInUrl);
  }

  // Return authenticated user object
  return user as T;
}

/**
 * Checks if the user is authenticated based on the provided tokens.
 *
 * @param {IntrasoftJwtTokens} tokens - The tokens to check.
 * @returns {boolean} True if the user is authenticated, false otherwise.
 */
function isAuthenticated(tokens: Partial<IntrasoftJwtTokens>): boolean {
  return Boolean(tokens.access || tokens.refresh);
}

/**
 * Re-fetches the tokens.
 *
 * @param {IntrasoftJwtTokens} tokens - The tokens to refetch.
 * @returns {Promise<void>} A promise that resolves when the tokens are re-fetched.
 */
async function refetchTokens(
  tokens: IntrasoftJwtTokens
): Promise<IntrasoftJwtTokens | undefined> {
  if (!tokens.refresh) {
    return;
  }
  return await revalidatedTokens(tokens.refresh);
}
